import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { ChangeEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { isBefore, startOfDay } from 'date-fns';
import { FormSpy } from 'react-final-form';

import { getDictionariesKnoOrganizationsSelectOptions } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IKnmErknmFormCollaboratingOrganization } from 'KnmPackage/MainModule/interfaces/IKnmErknmFormCollaboratingOrganization';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmErknmReasonsFormValue,
  getKnmExcludeDates,
  getKnmFormRef,
  getKnmIsErknmStatusFillInProcess,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import {
  getKnmCreatedDate,
  getKnmIsApprovedOnPaper,
  getKnmStartDate,
  getKnmStopDate,
} from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IErknmInfoExcludeDatesResponse } from 'KnmErknmPackage/ErknmInfoModule/interfaces/IErknmInfoExcludeDatesResponse';
import { IKnmErknmFormReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormReason';
import { HadToBeHowInSignMessage } from 'KnmErknmPackage/MainModule/components/HadToBeHowInSignMessage';

import styles from './ErknmCollaboratingOrganizations.module.scss';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';
import { ErknmFieldMark } from '../../../KnmErknmPackage/MainModule/components/ErknmFieldMark';
import { CollaboratingOrganisationsModal } from './CollaboratingOrganisationsModal';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { ErknmApproveRemarkAddButton } from '../../../KnmErknmPackage/ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';
import { ErknmApproveRemarkSectionCodeEnum } from '../../../KnmErknmPackage/ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { ErknmApproveRemark } from '../../../KnmErknmPackage/ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { ErknmApproveRemarkChanges } from '../../../KnmErknmPackage/ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { ErknmApprovedOnPaperAttachment } from './ErknmApprovedOnPaperAttachment';

interface IErknmCollaboratingOrganizationsProps {
  created: string | undefined;
  fieldsLabel: ReactNode;
  getOrganizationsItemBlockId: (id: string) => string;
  isHasCollaboratingOrganization: boolean | undefined;
  organizationsBlockId: string;
  watchOnly: boolean;
}

interface IErknmCollaboratingOrganizationsStoreProps {
  formRef: IKnmFormRef | undefined;
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  settings: IAdminSetting[];
  dateCreate: string | undefined;
  startDate: string | undefined;
  stopDate: string | undefined;
  excludeDates: IErknmInfoExcludeDatesResponse | undefined;
  reasons: IKnmErknmFormReason[] | undefined;
  isDecisionStatusFillInProcess: boolean;
  isApprovedOnPaper: boolean | undefined;
}

const ErknmCollaboratingOrganizationsWithoutStore = ({
  created,
  watchOnly,
  organizationsBlockId,
  fieldsLabel,
  isHasCollaboratingOrganization,
  getOrganizationsItemBlockId,
  formRef,
  knoOrganizationsSelectOptions,
  settings,
  dateCreate,
  startDate,
  stopDate,
  excludeDates,
  reasons,
  isDecisionStatusFillInProcess,
  isApprovedOnPaper,
}: IErknmCollaboratingOrganizationsProps & IErknmCollaboratingOrganizationsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const collaboratingOrganizations = formRef?.current.getFieldState('collaboratingOrganizationsErknm')?.value;
  const [isOrganisationsModalVisible, setIsOrganisationsModalVisible] = useState(false);
  const [isAgreedPaperVisible, setIsAgreedPaperVisible] = useState(false);

  const isOn411 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.COLLABORATION_KNO_ERVK_SEARCH_4_1_1);

  const addField = useCallback(() => {
    if (isOn411) {
      setIsOrganisationsModalVisible(true);
    } else if (!watchOnly && push && !isOn411) {
      const addingCollaboratingOrganization: IKnmErknmFormCollaboratingOrganization = {
        organization: undefined,
        guid: undefined,
        dateCreate: undefined,
        title: undefined,
        ervkKnoOrganizationId: undefined,
        ervkTerritoryAgencyId: undefined,
        ervkTerritoryAgencyVersionId: undefined,
        listId: uuidv4(),
      };

      push('collaboratingOrganizationsErknm', addingCollaboratingOrganization);
    }
  }, [push, watchOnly, isOn411]);

  const makeRemoveField = (index: number) => () =>
    !watchOnly && remove && remove('collaboratingOrganizationsErknm', index);

  const handleForeignSubjectAgencyChange = (v: ChangeEvent<HTMLInputElement>) => {
    formRef?.current.change('isHasCollaboratingOrganization', v.target.checked);
    if (!v.target.checked) {
      formRef?.current.change('collaboratingOrganizationsErknm', undefined);
    }
  };

  const handleApprovedPaperChange = (v: ChangeEvent<HTMLInputElement>) => {
    formRef?.current.change('isApprovedOnPaper', v.target.checked);
    if (!v.target.checked) {
      formRef?.current.change('approvedOnPaperDocuments', undefined);
    }
  };

  const handleOrganisationsModalApply = useCallback(
    (organisations: IKnmErknmFormCollaboratingOrganization[]) => {
      if (!watchOnly && push) {
        formRef?.current.change('collaboratingOrganizationsErknm', organisations);
      }

      setIsOrganisationsModalVisible(false);
    },
    [watchOnly, push, collaboratingOrganizations],
  );

  const handleOrganisationsModalClose = useCallback(() => {
    setIsOrganisationsModalVisible(false);
  }, []);

  useEffect(() => {
    if (!excludeDates || !stopDate || !dateCreate || !startDate || !reasons) {
      return;
    }

    const parseStartDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
    const parseStopDate = parseDate({ date: stopDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
    const parseCreateDate = parseDate({ date: dateCreate || '', parseFormat: DATE_FORMAT_LONG_FNS });

    const numberOfAvailableWorkdaysInPastIfSystemInoperability =
      excludeDates?.startDate.numberOfAvailableWorkdaysInPastIfSystemInoperability;

    if (
      !numberOfAvailableWorkdaysInPastIfSystemInoperability ||
      parseStartDate === undefined ||
      parseStopDate === undefined ||
      parseCreateDate === undefined
    ) {
      setIsAgreedPaperVisible(false);

      return;
    }

    const hasAnyReasonRequiringApproval = reasons.some(reason => reason.type?.agreement === 'Требуется');

    const startOfStartDate = startOfDay(parseStartDate);
    const startOfStopDate = startOfDay(parseStopDate);
    const startOfCreateDate = startOfDay(parseCreateDate);

    if (isBefore(startOfStartDate, startOfCreateDate) || isBefore(startOfStopDate, startOfCreateDate)) {
      setIsAgreedPaperVisible(hasAnyReasonRequiringApproval);

      return;
    }
    setIsAgreedPaperVisible(false);
  }, [stopDate, startDate, dateCreate, excludeDates]);

  useEffect(() => {
    if (formRef?.current && isApprovedOnPaper !== undefined) {
      const isApprovedOnPaperCurrentValue = formRef.current.getFieldState('isApprovedOnPaper')?.value;

      if (isApprovedOnPaperCurrentValue === undefined) {
        formRef.current.change('isApprovedOnPaper', isApprovedOnPaper);
      }
    }
  }, []);

  return (
    <div className={styles.Wrapper}>
      <Row justify="start" notWrap>
        <FieldBlock placementLabel="left" size="content">
          <FieldBlock placementLabel="left">
            <Checkbox
              className={styles.IsHasCollaboratingOrganizationField}
              labelClassName="shared-field-label_user-select"
              id="isHasCollaboratingOrganization"
              name="isHasCollaboratingOrganization"
              label="Проводится совместно"
              size="medium"
              type="checkbox"
              theme="system"
              disabled={watchOnly}
              checked={isHasCollaboratingOrganization === true}
              onChange={handleForeignSubjectAgencyChange}
            />
          </FieldBlock>

          <FormSpy subscription={{ values: true }}>
            {({ values }) => {
              const isApprovedOnPaperFormValue = values?.isApprovedOnPaper;

              return (
                <>
                  {isAgreedPaperVisible && (
                    <FieldBlock size="content">
                      <Checkbox
                        labelClassName="shared-field-label_user-select"
                        id="isApprovedOnPaper"
                        name="isApprovedOnPaper"
                        label="Согласовано на бумаге"
                        size="medium"
                        type="checkbox"
                        theme="system"
                        checked={isApprovedOnPaperFormValue}
                        onChange={handleApprovedPaperChange}
                        disabled={!isDecisionStatusFillInProcess}
                      />
                      <HadToBeHowInSignMessage
                        message={'При согласовании проверки на бумаге (неработоспособность ЕРКНМ)'}
                      />
                    </FieldBlock>
                  )}
                </>
              );
            }}
          </FormSpy>
        </FieldBlock>

        <ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.COLLABORATING_ORGANIZATIONS} />
        <FormSpy subscription={{ values: true }}>
          {({ values }) => {
            const isApprovedOnPaperFormValue = values?.isApprovedOnPaper === true;

            return (
              <>
                {isApprovedOnPaperFormValue && (
                  <ErknmApprovedOnPaperAttachment watchOnly={watchOnly} formRef={formRef} />
                )}
              </>
            );
          }}
        </FormSpy>
      </Row>

      <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.COLLABORATING_ORGANIZATIONS} />

      <CollaboratingOrganisationsModal
        visible={isOrganisationsModalVisible}
        selectedOrganisations={collaboratingOrganizations}
        onApply={handleOrganisationsModalApply}
        onClose={handleOrganisationsModalClose}
      />

      {isHasCollaboratingOrganization && (
        <FieldBlock
          label={fieldsLabel}
          id={organizationsBlockId}
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              variant="link"
              color="primary"
              type="button"
              disabled={watchOnly}
              onClick={addField}
            />
          }
        >
          <FieldArray name="collaboratingOrganizationsErknm">
            {({ fields }: FieldArrayRenderProps<IKnmErknmFormCollaboratingOrganization, HTMLElement>) => {
              if (!fields.length) {
                return null;
              }

              return (
                <Row>
                  {fields.map((fieldName, fieldIndex) => {
                    const fieldValue: IKnmErknmFormCollaboratingOrganization = fields.value?.[fieldIndex];
                    const id = getOrganizationsItemBlockId(fieldValue.listId);

                    return (
                      <Row key={id} margin="medium" isListItem>
                        {!isOn411 && (
                          <SelectInputField
                            className="shared-deleting-row"
                            name={`${fieldName}.organization`}
                            placeholder={
                              <>
                                Выберите наименование органа контроля
                                <ErknmFieldMark fieldName={`${fieldName}.organization`} />
                              </>
                            }
                            options={knoOrganizationsSelectOptions}
                            isSearchable
                            disabled={watchOnly}
                            size="medium"
                            id={id}
                          />
                        )}
                        {isOn411 && (
                          <TextInputField
                            className="shared-deleting-row"
                            name={`${fieldName}.title`}
                            placeholder={
                              <>
                                Выберите наименование органа контроля
                                <ErknmFieldMark fieldName={`${fieldName}.title`} />
                              </>
                            }
                            disabled={true}
                            size="medium"
                            id={id}
                            value={fieldValue.title}
                          />
                        )}
                        <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveField(fieldIndex)} />
                        <div className={styles.RemarkChanges}>
                          <ErknmApproveRemarkChanges
                            sectionCode={ErknmApproveRemarkSectionCodeEnum.COLLABORATING_ORGANIZATIONS}
                            guid={fieldValue.guid}
                          />
                        </div>
                      </Row>
                    );
                  })}
                </Row>
              );
            }}
          </FieldArray>
        </FieldBlock>
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IErknmCollaboratingOrganizationsStoreProps => ({
  formRef: getKnmFormRef(state),
  knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  settings: getAdminFetchSettings(state),
  dateCreate: getKnmCreatedDate(state),
  startDate: getKnmStartDate(state),
  stopDate: getKnmStopDate(state),
  excludeDates: getKnmExcludeDates(state),
  reasons: getKnmErknmReasonsFormValue(state),
  isDecisionStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isApprovedOnPaper: getKnmIsApprovedOnPaper(state),
});

export const ErknmCollaboratingOrganizations = connect(mapStateToProps)(ErknmCollaboratingOrganizationsWithoutStore);
